import { type ReactNode } from 'react'

type Tone =
  | 'neutral'
  | 'draft'
  | 'live'
  | 'finished'
  | 'closed'
  | 'archived'
  | 'success'
  | 'warn'
  | 'info'
  | 'gold'
  | 'accent'

type Props = {
  tone?: Tone
  iconLeft?: ReactNode
  children: ReactNode
  // Auto-true for `live`.
  pulse?: boolean
  size?: 'xs' | 'sm'
}

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  draft: 'bg-[var(--status-draft-bg)] text-[var(--status-draft-fg)]',
  live: 'bg-[var(--status-live-bg)] text-[var(--status-live-fg)]',
  finished: 'bg-[var(--status-finished-bg)] text-[var(--status-finished-fg)]',
  closed: 'bg-[var(--status-closed-bg)] text-[var(--status-closed-fg)]',
  archived: 'bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]',
  success: 'bg-green-50 text-green-800',
  warn: 'bg-[#fdf3d2] text-[#5f4807]',
  info: 'bg-info/15 text-info',
  gold: 'bg-gold/20 text-[#6e4f0e]',
  accent: 'bg-green-700 text-white',
}

const SIZE_CLASS = {
  xs: 'text-[10px] px-1.5 py-0.5',
  sm: 'text-xs px-2 py-1',
}

const Pill = ({
  tone = 'neutral',
  iconLeft,
  children,
  pulse,
  size = 'xs',
}: Props) => {
  const showPulseDot = pulse || tone === 'live'
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-caps ${TONE_CLASS[tone]} ${SIZE_CLASS[size]}`}
    >
      {showPulseDot && (
        <span
          aria-hidden="true"
          className="w-1.5 h-1.5 rounded-full bg-current"
          style={
            tone === 'live' || pulse
              ? { animation: 'polf-pulse 1.2s infinite' }
              : undefined
          }
        />
      )}
      {iconLeft}
      {children}
    </span>
  )
}

export default Pill
